import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import ContentRequired from "@/components/ContentRequired";
import { Plus, ArrowUpRight, CalendarClock, MapPin, Clock, Download, Mail } from "lucide-react";
import { toast } from "sonner";
import { formatApiErrorDetail } from "@/context/AuthContext";

const defaultForm = () => ({ day_number: 1, title: "", theme: "", date: "", objective: "", room: "", description: "" });

export default function DailyWorkbook() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [days, setDays] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(defaultForm());
  const [busy, setBusy] = useState(false);
  const isAdmin = user && (user.role === "admin" || user.role === "faculty");

  const load = async () => {
    const { data } = await api.get("/days");
    setDays(data);
    // When the dialog reopens, default day_number to the next slot.
    setForm((f) => ({ ...f, day_number: (data?.length || 0) + 1 }));
  };

  useEffect(() => { load(); }, []);

  const openNew = () => {
    setForm({ ...defaultForm(), day_number: (days?.length || 0) + 1 });
    setOpen(true);
  };

  const onCreate = async () => {
    // Basic client-side date sanity — accept YYYY-MM-DD only.
    if (form.date && !/^\d{4}-\d{2}-\d{2}$/.test(form.date)) {
      toast.error("Date should be YYYY-MM-DD");
      return;
    }

    if (!form.title.trim()) {
      toast.error("Title required");
      return;
    }
    setBusy(true);
    try {
      await api.post("/days", { ...form, day_number: Number(form.day_number) });
      toast.success("Session created");
      setOpen(false);
      setForm(defaultForm());
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not create session");
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-8" data-testid="workbook-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] font-semibold tracking-[0.16em] uppercase text-[#25AAAA]">Programme</div>
          <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter mt-2 text-[#0F1F2E]">Sessions</h1>
          <p className="text-[#525252] mt-2 max-w-2xl text-sm">
            Eight sessions across seven weeks. Click any session to open its brief, resources and outputs.
            {isAdmin && (
              <span className="block mt-2 text-[#25AAAA] font-medium">
                Admin: click a session, then <em>Edit day</em> to change the title, tagline, room and objective. Your edits are protected from future automatic updates.
              </span>
            )}
          </p>
        </div>
        <a
          href={`${process.env.REACT_APP_BACKEND_URL}/api/days/ics/all`}
          download
          className="inline-flex items-center gap-2 h-10 px-4 border-2 border-[#25AAAA] text-[#0F1F2E] font-semibold uppercase text-xs tracking-wider hover:bg-[#25AAAA] hover:text-white transition-colors rounded-none"
          data-testid="programme-all-sessions-ics-btn"
          title="Download one .ics with all 8 sessions"
        >
          <Download size={14} /> Add all sessions to calendar
        </a>
        {isAdmin && (
          <a
            href={`${process.env.REACT_APP_BACKEND_URL}/api/programme/export.md`}
            download
            className="inline-flex items-center h-10 px-4 border-2 border-[#0F1F2E] text-[#0F1F2E] font-semibold uppercase text-xs tracking-wider hover:bg-[#0F1F2E] hover:text-white transition-colors rounded-none"
            data-testid="programme-export-md-btn"
          >
            Export all sessions (.md)
          </a>
        )}
        {isAdmin && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button onClick={openNew} className="rounded-none bg-[#25AAAA] hover:bg-[#1e7d7d]" data-testid="workbook-add-day-btn">
                <Plus size={16} className="mr-2" /> Add a session
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-none max-w-2xl">
              <DialogHeader>
                <DialogTitle className="font-display tracking-tight">New session</DialogTitle>
              </DialogHeader>
              <div className="grid grid-cols-2 gap-4">
                <Field label="Session number" type="number" value={form.day_number} onChange={(v) => setForm((f) => ({ ...f, day_number: v }))} testid="day-form-number"/>
                <Field label="Date" type="date" value={form.date} onChange={(v) => setForm((f) => ({ ...f, date: v }))} placeholder="YYYY-MM-DD" testid="day-form-date"/>
                <Field label="Title" value={form.title} onChange={(v) => setForm((f) => ({ ...f, title: v }))} placeholder="e.g. Team Formation" testid="day-form-title"/>
                <Field label="Tagline / theme" value={form.theme} onChange={(v) => setForm((f) => ({ ...f, theme: v }))} placeholder="e.g. Your Innovation Consultants Now!" testid="day-form-theme"/>
                <div className="col-span-2">
                  <Label className="text-[10px] font-semibold tracking-[0.16em] uppercase text-[#525252]">Session objective</Label>
                  <Textarea value={form.objective} onChange={(e) => setForm((f) => ({ ...f, objective: e.target.value }))}
                            className="mt-2 rounded-none min-h-[100px]"
                            placeholder="What the team should achieve in this session."
                            data-testid="day-form-objective"/>
                </div>
                <div className="col-span-2">
                  <Field label="Room / delivery" value={form.room} onChange={(v) => setForm((f) => ({ ...f, room: v }))}
                    placeholder="Innovation Centre, Knowledge Gateway" testid="day-form-room"/>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" className="rounded-none" onClick={() => setOpen(false)}>Cancel</Button>
                <Button onClick={onCreate} disabled={busy} className="rounded-none bg-[#25AAAA] hover:bg-[#1e7d7d]" data-testid="workbook-day-save-btn">
                  {busy ? "Creating…" : "Create session"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {days.length === 0 ? (
        <ContentRequired
          title="No sessions configured yet"
          description="Add the sessions that make up the programme. Each session gets its own page with objective, tasks, resources and outputs."
          needed="Session numbers · titles · dates · themes · objectives"
          where="Sessions page + per-session detail"
          format="Add via the 'Add a session' button"
          testid="workbook-empty-content-required"
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-0 border-l border-t border-[#E5E7EB]">
          {days.map((d) => (
            <div key={d.id} className="border-r border-b border-[#E5E7EB] bg-white relative group hover:bg-[#F3F4F6] transition-colors" data-testid={`day-card-${d.day_number}`}>
              <Link to={`/workbook/${d.id}`} className="block p-6">
                <div className="flex items-start justify-between">
                  <div className="font-display text-5xl font-black tracking-tighter text-[#25AAAA]">
                    {String(d.day_number).padStart(2, "0")}
                  </div>
                  <ArrowUpRight size={18} className="opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>
                {d.date && (
                  <div className="text-[10px] font-semibold tracking-[0.16em] uppercase text-[#525252] mt-4 flex items-center gap-1.5">
                    <Clock size={11}/> {formatDayDate(d.date)} · 6:00–9:00 pm
                  </div>
                )}
                <div className="font-display font-bold text-lg tracking-tight mt-3 text-[#0F1F2E]">
                  {d.title || <span className="text-[#525252] italic">Untitled</span>}
                </div>
                {d.theme && <div className="text-xs text-[#25AAAA] font-semibold mt-1">{d.theme}</div>}
                {d.room && (
                  <div className="mt-3 flex flex-wrap items-center gap-2">
                    <div className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.14em] bg-[#E6F7F7] border border-[#25AAAA] text-[#1e7d7d] px-2 py-0.5 font-semibold"
                         data-testid={`day-card-${d.day_number}-room`}>
                      <MapPin size={9}/> {d.room}
                    </div>
                  </div>
                )}
                {d.objective && (
                  <p className="text-sm text-[#525252] mt-4 line-clamp-4 whitespace-pre-wrap">{d.objective}</p>
                )}
              </Link>
              {/* Session invite (.ics) — outside the Link so click doesn't navigate */}
              {d.date && (
                <a
                  href={`${process.env.REACT_APP_BACKEND_URL}/api/days/${d.id}/ics`}
                  download
                  onClick={(e) => e.stopPropagation()}
                  className="absolute bottom-3 right-3 inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.14em] bg-white border border-[#0F1F2E] text-[#0F1F2E] px-2 py-1 font-semibold hover:bg-[#0F1F2E] hover:text-white transition-colors"
                  data-testid={`day-card-${d.day_number}-ics`}
                  title="Add this session to your calendar (.ics)"
                >
                  <Download size={10} /> Add to calendar
                </a>
              )}
              {/* Bulk send admin action */}
              {isAdmin && d.date && (
                <button
                  type="button"
                  onClick={async (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (!window.confirm(`Email all attendees (students, mentors, sponsor reps) the calendar invite for ${d.title}?`)) return;
                    const t = toast.loading("Sending invites…");
                    try {
                      const { data } = await api.post(`/days/${d.id}/invite`);
                      toast.success(`Sent ${data.sent} of ${data.total}${data.failed ? ` · ${data.failed} failed` : ""}`, { id: t });
                    } catch (err) {
                      toast.error(err?.response?.data?.detail || "Send failed", { id: t });
                    }
                  }}
                  className="absolute bottom-3 left-3 inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.14em] bg-[#25AAAA] border border-[#25AAAA] text-white px-2 py-1 font-semibold hover:bg-[#1e7d7d] transition-colors"
                  data-testid={`day-card-${d.day_number}-invite`}
                  title="Email .ics to all attendees"
                >
                  <Mail size={10} /> Email attendees
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Field({ label, value, onChange, placeholder, type = "text", testid }) {
  return (
    <div>
      <Label className="text-[10px] font-semibold tracking-[0.16em] uppercase text-[#525252]">{label}</Label>
      <Input type={type} value={value} onChange={(e) => onChange(e.target.value)}
             placeholder={placeholder} className="mt-2 rounded-none h-11"
             data-testid={testid}/>
    </div>
  );
}

function formatDayDate(iso) {
  if (!iso) return "";
  const d = new Date(iso + "T00:00:00");
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("en-GB", { weekday: "short", day: "2-digit", month: "short", year: "numeric" });
}
